import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { LocationsApiAdapter } from '../adapters/LocationsApiAdapter';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useLocateReturned } from './useLocateReturned';

interface ProductToLocate {
  id: string;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location?: string;
  image?: string;
}

interface LocatedProduct {
  id: string;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location: string;
  image?: string;
}

interface UseLocateLogicProps {
  returnedId: number;
  processedProducts?: any[];
  processedProductsQuantities?: Record<string, number>;
  processedProductsTotals?: Record<string, number>;
}

export const useLocateLogic = ({
  returnedId,
  processedProducts = [],
  processedProductsQuantities = {},
  processedProductsTotals = {},
}: UseLocateLogicProps) => {
  const { toast } = useToast();
  const { t } = useTranslation('receiveModule');

  // 🎯 Hook para ubicar productos
  const { locateProductsAsync, isLocating } = useLocateReturned(returnedId);

  // 🎯 Adaptador para ubicaciones
  const locationsAdapter = useMemo(
    () => new LocationsApiAdapter(createHttpClient(apiClientWH)),
    []
  );

  // 🎯 Estado inicial vacío
  const [productsToLocate, setProductsToLocate] = useState<ProductToLocate[]>(
    []
  );

  // 🎯 Estado para la ubicación escaneada actual
  const [currentLocation, setCurrentLocation] = useState<{
    id: number;
    code: string;
  } | null>(null);

  // 🎯 Estado de loading para la validación de ubicación
  const [isValidatingLocation, setIsValidatingLocation] =
    useState<boolean>(false);

  // 🎯 Estado para el producto siendo ubicado
  const [productBeingLocated, setProductBeingLocated] =
    useState<ProductToLocate | null>(null);

  // 🎯 Estado para la cantidad a ubicar
  const [quantityToLocate, setQuantityToLocate] = useState<number>(0);

  // 🎯 Ref para controlar la inicialización y evitar bucles infinitos
  const initRef = useRef<{ initialized: boolean; returnedId: number | null; lastProcessedLength: number }>({
    initialized: false,
    returnedId: null,
    lastProcessedLength: 0,
  });

  useEffect(() => {
    const shouldInit = 
      !initRef.current.initialized || 
      initRef.current.returnedId !== returnedId ||
      initRef.current.lastProcessedLength !== processedProducts.length;

    if (!shouldInit || processedProducts.length === 0) return;

    const newProductsToLocate = processedProducts.map((product, index) => ({
      id: String(product.id ?? `product-${index}`),
      name: product.name || `Producto ${product.code}`,
      code: product.code,
      quantity: processedProductsQuantities[product.code] || 0,
      totalQuantity: processedProductsTotals[product.code] || 0,
      location: undefined,
      image: product.image || '/imagen/example.webp',
    }));

    setProductsToLocate(newProductsToLocate);
    
    // Actualizar el ref
    initRef.current = {
      initialized: true,
      returnedId,
      lastProcessedLength: processedProducts.length,
    };
  }, [
    processedProducts,
    processedProductsQuantities,
    processedProductsTotals,
    returnedId,
  ]);

  const [locatedProducts, setLocatedProducts] = useState<LocatedProduct[]>([]);
  const [toLocateSortBy, setToLocateSortBy] = useState<string>('more_qty');
  const [locatedSortBy, setLocatedSortBy] = useState<string>('more_qty');

  // 🎯 Función para escanear ubicación o producto
  const handleLocationScan = useCallback(
    async (code: string) => {
      // Si ya hay una ubicación, buscar el producto
      if (currentLocation) {
        // Buscar el producto en la lista por código
        const foundProduct = productsToLocate.find((p) => p.code === code);

        if (foundProduct) {
          // Mostrar bloque para ubicar el producto y contar el primer escaneo
          setProductBeingLocated(foundProduct);
          setQuantityToLocate(1); // ✅ Iniciar en 1 al escanear el producto

          // ✅ Mostrar toast de unidad agregada al reconocer el primer escaneo del producto
          toast({
            title: t('toasts.unit_added.title'),
            description: t('toasts.unit_added.description'),
            variant: 'default',
          });
        } else {
          // Si no está por ubicar, verificar si ya fue ubicado
          const alreadyLocated = locatedProducts.find((p) => p.code === code);
          if (alreadyLocated) {
            toast({
              title: t('toasts.product_already_located.title'),
              description: t('toasts.product_already_located.description', {
                product: alreadyLocated.name,
              }),
              variant: 'destructive',
            });
          } else {
            // Producto no encontrado en ninguna lista
            toast({
              title: t('toasts.product_not_found.title'),
              description: t('toasts.product_not_found.description', { code }),
              variant: 'destructive',
            });
          }
        }
        return;
      }

      // Si no hay ubicación, validar la ubicación escaneada
      setIsValidatingLocation(true);

      try {
        // Validar ubicación con la API
        const response = await locationsAdapter.getLocationByCode(code);

        if (response.success && response.data) {
          // Ubicación válida - guardar en el estado
          setCurrentLocation({
            id: response.data.id,
            code: response.data.code,
          });
          // ✅ Sin toast de confirmación para evitar confusión
        } else {
          // Ubicación no encontrada - mostrar error
          toast({
            title: t('toasts.location_error.title'),
            description:
              response.message || t('toasts.location_error.description'),
            variant: 'destructive',
          });
        }
      } catch {
        // Error inesperado
        toast({
          title: t('toasts.location_error.title'),
          description: t('toasts.location_error.unexpected'),
          variant: 'destructive',
        });
      } finally {
        setIsValidatingLocation(false);
      }
    },
    [
      currentLocation,
      productsToLocate,
      locatedProducts,
      toast,
      t,
      locationsAdapter,
    ]
  );

  // 🎯 Función para escanear unidades (código del producto)
  const handleScanUnits = useCallback(
    (scannedCode: string) => {
      if (!productBeingLocated) return;

      // Verificar si el código escaneado coincide con el producto actual
      if (scannedCode !== productBeingLocated.code) {
        toast({
          title: t('toasts.wrong_product_code.title'),
          description: t('toasts.wrong_product_code.description'),
          variant: 'destructive',
        });
        return;
      }

      // Si es el código correcto, sumar 1 unidad
      const newQuantity = Math.min(
        quantityToLocate + 1,
        productBeingLocated.quantity
      );
      setQuantityToLocate(newQuantity);

      // Mostrar toast informativo
      if (newQuantity >= productBeingLocated.quantity) {
        toast({
          title: t('toasts.max_quantity_reached.title'),
          description: t('toasts.max_quantity_reached.description'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('toasts.unit_added.title'),
          description: t('toasts.unit_added.description'),
          variant: 'default',
        });
      }
    },
    [productBeingLocated, quantityToLocate, toast, t]
  );

  // 🎯 Función para ubicar un producto (llama a la API línea por línea)
  const handleProductLocate = useCallback(
    async (
      productId: string,
      locationId: number,
      quantity: number,
      productCode?: string
    ) => {
      // Encontrar el producto en la lista de productos por ubicar
      const productIndex = productsToLocate.findIndex(
        (p) =>
          String(p.id) === String(productId) ||
          (!!productCode && p.code === productCode)
      );
      if (productIndex === -1) {
        toast({
          title: t('toasts.locate.error'),
          description: t('toasts.product_not_found.description', { code: productCode }),
          variant: 'destructive',
        });
        return;
      }

      const product = productsToLocate[productIndex];

      try {
        console.log('🔍 useLocateLogic - Locating product:', {
          productId,
          locationId,
          quantity,
          productCode,
        });

        // 🎯 Llamar a la API con una sola línea
        await locateProductsAsync({
          lines: [
            {
              product_id: parseInt(product.id),
              quantity: quantity,
              location_id: locationId,
            },
          ],
        });

        console.log('🔍 useLocateLogic - Product located successfully');

        // 🎯 Solo actualizar estado local si la API fue exitosa
        const locatedProduct: LocatedProduct = {
          id: product.id,
          name: product.name,
          code: product.code,
          quantity: quantity,
          totalQuantity: product.totalQuantity,
          location: currentLocation?.code || String(locationId),
          image: product.image,
        };

        // Remover de productos por ubicar y agregar a ubicados
        setProductsToLocate((prev) =>
          prev.filter(
            (p) =>
              String(p.id) !== String(productId) &&
              (!productCode || p.code !== productCode)
          )
        );
        setLocatedProducts((prev) => [...prev, locatedProduct]);

        // 🎯 Limpiar estado para volver al paso 1
        setProductBeingLocated(null);
        setQuantityToLocate(0);
        setCurrentLocation(null); // ✅ Limpiar ubicación para poder elegir otra
      } catch (error) {
        // El error ya se maneja en useLocateReturned, solo logueamos aquí
        console.log('🔍 useLocateLogic - Error locating product:', error);
        // No actualizamos el estado local si hay error
      }
    },
    [
      productsToLocate,
      currentLocation,
      locateProductsAsync,
      toast,
      t,
    ]
  );

  // 🎯 Función para limpiar ubicación manualmente
  const handleClearLocation = useCallback(() => {
    setCurrentLocation(null);
    setProductBeingLocated(null);
    setQuantityToLocate(0);

    toast({
      title: t('toasts.location_cleared.title'),
      description: t('toasts.location_cleared.description'),
      variant: 'default',
    });
  }, [toast, t]);

  // 🎯 Función para finalizar ubicación (ya no llama a la API, solo verifica y finaliza)
  const handleFinishLocated = useCallback(() => {
    if (productsToLocate.length > 0) {
      toast({
        title: t('toasts.finish_warning.title'),
        description: t('toasts.finish_warning.description'),
        variant: 'destructive',
      });
      return;
    }

    // 🎯 Ya no se llama a la API aquí, las ubicaciones se hacen línea por línea al presionar "Ubicar"
    // Solo mostrar mensaje de éxito y redirigir (la navegación se maneja en el componente)
    toast({
      title: t('locate.finished_title'),
      description: t('locate.finished_message'),
      variant: 'default',
    });
  }, [toast, t, productsToLocate.length]);

  // 🎯 Funciones de ordenamiento
  const handleToLocateSort = useCallback((sortBy: string) => {
    setToLocateSortBy(sortBy);
  }, []);

  const handleLocatedSort = useCallback((sortBy: string) => {
    setLocatedSortBy(sortBy);
  }, []);

  // 🎯 Productos ordenados
  const sortedProductsToLocate = useMemo(() => {
    return [...productsToLocate].sort((a, b) => {
      switch (toLocateSortBy) {
        case 'more_qty':
          return b.quantity - a.quantity;
        case 'less_qty':
          return a.quantity - b.quantity;
        case 'name_az':
          return a.name.localeCompare(b.name);
        case 'name_za':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });
  }, [productsToLocate, toLocateSortBy]);

  const sortedLocatedProducts = useMemo(() => {
    return [...locatedProducts].sort((a, b) => {
      switch (locatedSortBy) {
        case 'more_qty':
          return b.quantity - a.quantity;
        case 'less_qty':
          return a.quantity - b.quantity;
        case 'name_az':
          return a.name.localeCompare(b.name);
        case 'name_za':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });
  }, [locatedProducts, locatedSortBy]);

  return {
    productsToLocate: sortedProductsToLocate,
    locatedProducts: sortedLocatedProducts,
    toLocateSortBy,
    locatedSortBy,
    currentLocation,
    isValidatingLocation,
    productBeingLocated,
    quantityToLocate,
    setProductBeingLocated,
    setQuantityToLocate,
    handleLocationScan,
    handleScanUnits,
    handleProductLocate,
    handleClearLocation,
    handleToLocateSort,
    handleLocatedSort,
    handleFinishLocated,
    isLocating,
  };
};
